<template>
    <div v-if="financeAvailable !== false" id="finance">
        <h2 class="new-details__section-title">Personalise my finance</h2>
        <p class="new-details__section-subtitle">Select your finance option:</p>
        <LoadingSpinner v-if="loading"></LoadingSpinner>
        <div class="finance-calculator__container" v-show="!loading">
            <div class="finance-calculator__finance-types" v-if="multipleFinanceAvailable">
                <button v-if="pcp.available" @click="setSelectedFinance('pcp')" type="button" class="btn finance-calculator__finance-type-btn" :class="{'finance-calculator__finance-type-btn--selected': this.selectedFinance == 'pcp'}">Personal Contract Purchase</button>
                <button v-if="hp.available" @click="setSelectedFinance('hp')" type="button" class="btn finance-calculator__finance-type-btn" :class="{'finance-calculator__finance-type-btn--selected': this.selectedFinance == 'hp'}">Conditional Sale</button>
                <button v-if="vehicle.have_pch == 1" @click="setSelectedFinance('pch')" type="button" class="btn finance-calculator__finance-type-btn" :class="{'finance-calculator__finance-type-btn--selected': this.selectedFinance == 'pch'}">Personal Contract Hire</button>
            </div>
            <div class="finance-calculator__pch-container" v-show="selectedFinance == 'pch'">
                <div class="flex-row">
                    <div class="flex-xs-12 flex-sm-6">
                        <picture v-if="cmsImage !== false">
                            <source v-if="pchOffer.large_image_webp_2x" type="image/webp" :srcset="pchOffer.large_image_webp + ' 1x, ' + pchOffer.large_image_webp_2x + ' 2x'">
                            <source v-else-if="pchOffer.large_image_webp" type="image/webp" :srcset="pchOffer.large_image_webp">
                            <img v-if="pchOffer.large_image_jpg_2x" :src="pchOffer.large_image_jpg + ' 1x, ' + pchOffer.large_image_jpg_2x + ' 2x'" class="img-responsive" itemprop="thumbnail" alt="">
                            <img v-else :src="pchOffer.large_image_jpg" class="img-responsive finance-calculator__pch-img" itemprop="thumbnail" alt="">
                        </picture>
                        <picture v-else-if="image !== false">
                            <source type="image/webp" :srcset="image + '&fileType=webp'">
                            <img :src="image + '&fileType=png'" class="img-responsive" itemprop="thumbnail" alt="">
                        </picture>
                    </div>
                    <div class="flex-xs-12 flex-sm-6">
                        <div class="rd-card finance-calculator__finance-card  finance-calculator__finance-card--pch">
                            <h3 class="finance-calculator__pch-heading">Personal Contract Hire</h3>
                            <h3 class="finance-calculator__quote-title finance-calculator__quote-title--pch">{{ vehicle.vehicle_title }}</h3>

                            <div class="finance-calculator__card-item">
                                <span class="finance-calculator__card-item-label">
                                    <span class="finance-calculator__monthly-payment">{{ vehicle.pch_term }} Monthly</span> Payments
                                </span>
                                <span class="finance-calculator__card-item-value">
                                    {{ pchMonthly }}
                                </span>
                            </div>

                            <div class="finance-calculator__card-item">
                                <span class="finance-calculator__card-item-label">
                                    Initial Rental
                                </span>
                                <span class="finance-calculator__card-item-value">
                                    {{ pchInitialRental }}
                                </span>
                            </div>

                            <div class="finance-calculator__card-item" v-if="vehicle.pch_saving > 0">
                                <span class="finance-calculator__card-item-label">
                                    Customer Saving
                                </span>
                                <span class="finance-calculator__card-item-value">
                                    {{ pchSaving }}
                                </span>
                            </div>

                            <div class="finance-calculator__card-item">
                                <span class="finance-calculator__card-item-label">
                                    Annual Mileage
                                </span>
                                <span class="finance-calculator__card-item-value">
                                    {{ pchMileage }} miles
                                </span>
                            </div>

                            <div class="btn-container btn-container--left btn-container--vertical-xs">
                                <button @click="sendPCHEnquiry" type="button" class="btn btn--full-width-xs btn--primary">Enquire now</button>
                                <button @click="scrollToFinance" type="button" class="btn btn--full-width-xs btn--primary-inverted">Value my car</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="finance-calculator__pch-terms">
                    ^Personal Contract Hire on {{ vehicle.new_model.model_title }} {{ vehicle.version }}. Offer subject to availability. Initial rental {{ pchInitialRental }}, plus {{ vehicle.pch_term }} at {{ pchMonthly }}. {{ pchMileage }} miles per year. Excess mileage chargeable. Finance subject to status. T&Cs and exclusions apply. Applicants must be 18+. Guarantee may be required. You will not own the vehicle. Orders from {{ vehicle.pch_term_from }} to {{ vehicle.pch_term_to }}. Stellantis &You UK Limited is acting as a credit broker and not a lender. We may introduce you to a limited number of lenders. <a href="/terms-and-conditions" target="_blank">Ts and Cs apply</a>
                </div>
            </div>

            <div class="finance-calculator__blue-box" v-show="selectedFinance != 'pch'">
                <h3 class="finance-calculator__blue-box-title" v-if="selectedFinance == 'pcp'">Personal Contract Purchase</h3>
                <h3 class="finance-calculator__blue-box-title" v-else-if="selectedFinance == 'hp'">Conditional Sale</h3>
                <h3 class="finance-calculator__blue-box-title" v-else>Finance Calculator</h3>

                <div class="finance-calculator__slider-container">
                    <div class="finance-calculator__slider-label">Choose your term (months):</div>
                    <div class="finance-calculator__slider-value">{{ selectedTerm }} months</div>
                    <div ref="term-slider"></div>
                </div>

                <div class="finance-calculator__slider-container">
                    <div class="finance-calculator__slider-label">Choose your deposit:</div>
                    <div class="finance-calculator__slider-value">{{ formattedDeposit }}</div>
                    <div ref="deposit-slider"></div>
                </div>

                <div class="finance-calculator__slider-container" v-show="selectedFinance == 'pcp'">
                    <div class="finance-calculator__slider-label">Choose your annual mileage (miles):</div>
                    <div class="finance-calculator__slider-value">{{ formattedMileage }}</div>
                    <div ref="mileage-slider"></div>
                </div>

                <button @click="sendEnquiry" type="button" class="btn btn--primary finance-calculator__btn">Enquire now</button>
            </div>

            <div class="finance-calculator__calculated-payments" v-show="selectedFinance != 'pch'">
                <LoadingSpinner v-if="financeLoading"></LoadingSpinner>
                <div class="rd-card finance-calculator__finance-card" v-else-if="quote.HasError">
                    <div class="validation-error__li validation-error--danger">Please contact us directly for finance information.</div>
                </div>
                <div class="rd-card finance-calculator__finance-card" v-else>
                    <h3 class="finance-calculator__quote-title">Your Quote</h3>

                    <div class="finance-calculator__card-item" v-for="(item, index) in quoteAsArrayToRender" :key="index">
                        <span class="finance-calculator__card-item-label" v-html="item.title"></span>
                        <span class="finance-calculator__card-item-value" v-html="item.value"></span>
                    </div>
                </div>
            </div>

            <div class="finance-calculator__terms" v-if="selectedFinance != 'pch' && !financeLoading && !quote.HasError">
                ^<template v-if="financeModified">This is your personal quote, we will retain your deposit as you move around the website. </template>This quote is valid for a maximum of 14 days from <i>{{ quoteValidDate }} and may be withdrawn without notice</i>. Finance subject to status and age limitations. Stellantis Financial Services UK Limited RH1 1QA. <b>Stellantis &You UK Limited, trading as Stellantis &You Sales & Services, is acting as a broker not a lender and will introduce you to a limited number of lenders.</b> <a href="/terms-and-conditions" target="_blank">Ts and Cs apply</a>
                <template v-if="vehicle.brand == 'peugeot' && quoteWithPredictableKeys.dealerDepCon > 0">*Peugeot part exchange boost is applied to the purchasing vehicle and will be applied on cash or finance.  To receive the boost you must part exchange a vehicle.</template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import LoadingSpinner from '../../../core/components/LoadingSpinner.vue';
import noUiSlider from "nouislider";
import NumberFormat from "../../../used-vue/api/NumberFormat";
import getFinanceNameFromKey from "../../api/finance/GetFinanceNameFromKey";
import { getPayments } from "../../api/finance/finance-helpers";

export default {
    props: {},
    components: {
        LoadingSpinner,
    },
    data() {
        return {
            termSlider: null,
            depositSlider: null,
            mileageSlider: null,
        };
    },
    watch: {
        selectedFinance(val, oldVal) {
            if (val === null) {
                return;
            }

            if (val == 'pch') {
                return;
            }

            if (!this.availableParameters[val].available) {
                return;
            }

            // Create an array of ranges that has a fixed % from 0 to 100 for each available term on the slider
            const minTerm = this.availableParameters[val].terms[0];
            const termsLength = this.availableParameters[val].terms.length;
            const maxTerm = this.availableParameters[val].terms[termsLength - 1];
            const termDiff = maxTerm - minTerm;
            const range = {};
            if (this.availableParameters[val].terms.length > 1) {
                this.availableParameters[val].terms.forEach((term, index) => {
                    if (index == 0) {
                        range['min'] = [term];
                        return;
                    }
                    if (index == termsLength - 1) {
                        range['max'] = [term];
                        return;
                    }
                    const percent = Math.round((100 / termDiff) * (term - minTerm));
                    range[percent + '%'] = [term];
                });
            } else {
                range['min'] = [this.availableParameters[val].terms[0]];
                range['max'] = [this.availableParameters[val].terms[0]];
            }

            if (this.termSlider === null) {
                const initialTerm = this.closest(this.selectedTerm, this.availableParameters[val].terms);

                let pips = {
                    mode: 'steps',
                    density: 100,
                };

                if (termsLength >= 6) {
                    pips = {
                        mode: 'count',
                        values: 6,
                        density: 20,
                    };
                    console.log(pips);
                }

                this.termSlider = noUiSlider.create(this.$refs['term-slider'], {
                    start: [initialTerm],
                    connect: 'lower',
                    range: range,
                    snap: true,
                    pips
                });
                this.termSlider.on('update', values => {
                    this.$store.commit('Finance/setSelectedTerm', parseInt(values[0]));
                })
            } else {
                let pips = {
                    mode: 'steps',
                    density: 100,
                };

                if (termsLength >= 6) {
                    pips = {
                        mode: 'count',
                        values: 6,
                        density: 20,
                    };
                }
                this.termSlider.updateOptions({
                    range,
                    pips
                });
            }

            // Create an array of ranges that has a fixed % from 0 to 100 for each available term on the slider
            if (this.depositSlider === null) {
                let initialDeposit = this.selectedDeposit;
                let keepQueryStringDeposit = false;
                if (typeof this.$route.query['deposit'] != 'undefined' && parseInt(initialDeposit) == parseInt(this.$route.query['deposit'])) {
                    keepQueryStringDeposit = true;
                }
                if (this.selectedDeposit < this.availableParameters[val].minDeposit) {
                    initialDeposit = this.availableParameters[val].minDeposit;
                }
                if (this.selectedDeposit > this.availableParameters[val].maxDeposit) {
                    initialDeposit = this.availableParameters[val].maxDeposit;
                }
                this.depositSlider = noUiSlider.create(this.$refs['deposit-slider'], {
                    start: [initialDeposit],
                    connect: 'lower',
                    range: {
                        min: this.availableParameters[val].minDeposit,
                        max: this.availableParameters[val].maxDeposit,
                    },
                    step: 100,
                    pips: {
                        mode: 'count',
                        values: 6,
                        density: 20,
                        format: {
                            to: (value) => {
                                if (value == this.availableParameters[val].minDeposit) {
                                    return '£' + NumberFormat(value, 0);
                                }
                                if (value == this.availableParameters[val].maxDeposit) {
                                    return '£' + NumberFormat(value, 0);
                                }
                                return '';
                            },
                            from: function (value) {
                                return value;
                            }
                        }
                    }
                });
                this.depositSlider.on('update', values => {
                    if (keepQueryStringDeposit && parseInt(values[0]) != initialDeposit) {
                        keepQueryStringDeposit = false;
                        return;
                    }
                    this.$store.commit('Finance/setSelectedDeposit', parseInt(values[0]));
                })
            } else {
                this.depositSlider.updateOptions({
                    range: {
                        min: this.availableParameters[val].minDeposit,
                        max: this.availableParameters[val].maxDeposit,
                    }},
                );
            }


            if (val == 'pcp') {
                // Create an array of ranges that has a fixed % from 0 to 100 for each available term on the slider
                const minMileage = this.availableParameters[val].mileages[0];
                const mileagesLength = this.availableParameters[val].mileages.length;
                const maxMileage = this.availableParameters[val].mileages[mileagesLength - 1];
                const mileageDiff = maxMileage - minMileage;
                const mileageRange = {};
                this.availableParameters[val].mileages.forEach((mileage, index) => {
                    if (index == 0) {
                        mileageRange['min'] = [mileage];
                        return;
                    }
                    if (index == mileagesLength - 1) {
                        mileageRange['max'] = [mileage];
                        return;
                    }
                    const percent = Math.round((100 / mileageDiff) * (mileage - minMileage));
                    mileageRange[percent + '%'] = [mileage];
                });

                if (this.mileageSlider === null) {
                    const initialMileage = this.closest(this.selectedMileage, this.availableParameters[val].mileages);
                    this.mileageSlider = noUiSlider.create(this.$refs['mileage-slider'], {
                        start: [initialMileage],
                        connect: 'lower',
                        range: mileageRange,
                        snap: true,
                        pips: {
                            mode: 'count',
                            values: 6,
                            density: 20,
                            format: {
                                // 'to' the formatted value. Receives a number.
                                to: (value) => {
                                    if (value == minMileage) {
                                        return NumberFormat(value, 0);
                                    }
                                    if (value == maxMileage) {
                                        return NumberFormat(value, 0);
                                    }
                                    return '';
                                },
                                // 'from' the formatted value.
                                // Receives a string, should return a number.
                                from: function (value) {
                                    return value;
                                }
                            }
                        }
                    });
                    this.mileageSlider.on('update', values => {
                        this.$store.commit('Finance/setSelectedMileage', parseInt(values[0]));
                    })
                } else {
                    this.mileageSlider.updateOptions({
                        range: mileageRange
                    });
                }
            }

            this.$store.dispatch('Finance/calculateFinance');
        },
        selectedTerm(value) {
            this.$store.dispatch('Finance/calculateFinance');
        },
        selectedDeposit(value) {
            this.$store.dispatch('Finance/calculateFinance');
        },
        selectedMileage(value) {
            this.$store.dispatch('Finance/calculateFinance');
        }
    },
    computed: {
        ...mapState('NewVehicleData', ['vehicle', 'selectedPaint', 'pchOffer']),
        ...mapState('Finance', [
            'loading',
            'financeLoading',
            'financeAvailable',
            'availableParameters',
            'selectedFinance',
            'selectedTerm',
            'selectedDeposit',
            'selectedMileage',
            'financeModified',

            'quote',
            'quoteValidDate',
            'quoteWithPredictableKeys',
        ]),
        cmsImage() {
            if (this.pchOffer == null) {
                return false;
            }
            if (typeof this.pchOffer.large_image_jpg == 'undefined') {
                return false;
            }
            if (this.pchOffer.large_image_jpg == null) {
                return false;
            }
            if (this.pchOffer.large_image_jpg == '') {
                return false;
            }
            return true;
        },
        image() {
            if (!this.selectedPaint) {
                return false;
            }

            return 'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=01&' + this.selectedPaint.url_identifier;
        },
        pchMonthly() {
            if (!this.vehicle.have_pch) {
                return '';
            }
            return '£' + NumberFormat(this.vehicle.pch_monthly_payment, 0);
        },
        pchSaving() {
            if (!this.vehicle.have_pch) {
                return '';
            }
            return '£' + NumberFormat(this.vehicle.pch_saving, 0);
        },
        pchInitialRental() {
            if (!this.vehicle.have_pch) {
                return '';
            }
            return '£' + NumberFormat(this.vehicle.pch_initial_rental, 0);
        },
        pchMileage() {
            if (!this.vehicle.have_pch) {
                return '';
            }
            return NumberFormat(this.vehicle.pch_mileage, 0);
        },
        pcp() {
            return this.availableParameters.pcp;
        },
        hp() {
            return this.availableParameters.hp;
        },
        multipleFinanceAvailable() {
            let count = 0;
            if (this.pcp.available) {
                count++;
            }
            if (this.hp.available) {
                count++;
            }
            if (this.vehicle.have_pch == 1) {
                count++;
            }
            return count >= 1;
        },
        formattedDeposit() {
            return '£' + NumberFormat(this.selectedDeposit, 0);
        },
        formattedMileage() {
            return NumberFormat(this.selectedMileage, 0) + ' miles';
        },
        longFinanceName() {
            return getFinanceNameFromKey(this.vehicle, this.quote.Key, false);
        },
        depConName() {
            if (this.vehicle.fuel_type == 'Electric') {
                if (this.quoteWithPredictableKeys.depCon != 3000) {
                    return 'Stellantis &You UK Deposit Contribution';
                }
                if (this.vehicle.brand == 'fiat') {
                    return 'Fiat e-Grant';
                }
                if (this.vehicle.brand == 'abarth') {
                    return 'Abarth e-Grant';
                }
                return 'Stellantis &You UK Deposit Contribution';
            }
            return 'Stellantis &You UK Deposit Contribution';
        },
        quoteAsArrayToRender() {
            if (this.quote.HasError) {
                return [];
            }

            const payments = getPayments(this.quote);

            let outputArray = [];
            if (this.quote.Key == 'PCP') {
                payments.forEach(payment => {
                    outputArray.push({
                        title: '<span class="finance-calculator__monthly-payment">' + payment.quantity + ' Monthly</span> Payments',
                        emailTitle: payment.quantity + ' Monthly Payments',
                        value: '&pound;' + NumberFormat(payment.amount, 2)
                    });
                });

                outputArray.push({
                    title: 'Customer Cash Deposit',
                    value: '&pound;' + NumberFormat(this.quote.Quote.CashDeposit, 2)
                });

                if (this.vehicle.brand == 'peugeot' && this.quoteWithPredictableKeys.dealerDepCon > 0) {
                    outputArray.push({
                        title: 'Part Exchange Boost*',
                        value: '&pound;' + NumberFormat(this.quoteWithPredictableKeys.dealerDepCon, 2)
                    });
                    if (this.quoteWithPredictableKeys.depConExcludingDealer > 0) {
                        outputArray.push({
                            title: this.depConName,
                            value: '&pound;' + NumberFormat(this.quoteWithPredictableKeys.depConExcludingDealer, 2)
                        });
                    }
                } else if (this.quoteWithPredictableKeys.depCon > 0) {
                    outputArray.push({
                        title: this.depConName,
                        value: '&pound;' + NumberFormat(this.quoteWithPredictableKeys.depCon, 2)
                    });
                }

                outputArray = this.appendVehiclePricingIncludingApplicableGrants(outputArray);

                if (this.vehicle.brand !== 'vauxhall') {
                    outputArray.push({
                        title: this.longFinanceName + ' Price <span class="finance-calculator__small-label">Including &pound;' + this.quoteWithPredictableKeys.roadsideAssistance + ' Roadside Assistance</span>',
                        emailTitle: this.longFinanceName + ' Price - Including &pound;' + this.quoteWithPredictableKeys.roadsideAssistance + ' Roadside Assistance',
                        value: '&pound;' + NumberFormat(this.quote.Quote.TotalPrice, 2)
                    });
                }

                outputArray.push({
                    title: 'Amount Financed',
                    value: '&pound;' + NumberFormat(this.quote.Quote.Balance, 2)
                });

                outputArray.push({
                    title: 'Optional Final Payment To Buy',
                    value: '&pound;' + NumberFormat(this.quoteWithPredictableKeys.finalPayment, 2)
                });

                outputArray.push({
                    title: 'Credit Facility Fee <span class="finance-calculator__small-label">Payable With 1st Payment</span>',
                    emailTitle: 'Credit Facility Fee - Payable With 1st Payment',
                    value: '&pound;' + NumberFormat(this.quoteWithPredictableKeys.firstFee, 2)
                });

                outputArray.push({
                    title: 'Term Of Agreement',
                    value: this.quote.Quote.Term + ' months'
                });

                outputArray.push({
                    title: 'Total Charge For Credit',
                    value: '&pound;' + NumberFormat(this.quote.Quote.TotalChargeForCredit, 2)
                });

                outputArray.push({
                    title: 'Total Amount Of Credit',
                    value: '&pound;' + NumberFormat(this.quote.Quote.AmountOfCredit, 2)
                });

                outputArray.push({
                    title: 'Total Amount Payable',
                    value: '&pound;' + NumberFormat(this.quote.Quote.TotalAmountPayable, 2)
                });

                outputArray.push({
                    title: 'Fixed Rate of Interest',
                    value: NumberFormat(this.quote.Quote.RateOfInterest, 2) + '%'
                });

                if (this.financeModified) {
                    outputArray.push({
                        title: 'APR',
                        value: NumberFormat(this.quoteWithPredictableKeys.apr, 2) + '%'
                    });
                } else {
                    outputArray.push({
                        title: 'Representative APR',
                        value: NumberFormat(this.quoteWithPredictableKeys.apr, 2) + '%'
                    });
                }

                outputArray.push({
                    title: 'Excess Mileage Charge',
                    value: ' ' + NumberFormat(this.quote.Quote.ExcessMileageRate,0) + ' pence per mile'
                });

                outputArray.push({
                    title: 'Annual Mileage',
                    value: ' ' + NumberFormat(this.quote.Quote.AnnualMileage,0) + '  miles'
                });

                return outputArray;
            }

            if (this.quote.Key == 'HP') {
                payments.forEach(payment => {
                    outputArray.push({
                        title: '<span class="finance-calculator__monthly-payment">' + payment.quantity + ' Monthly</span> Payments',
                        emailTitle: payment.quantity + ' Monthly Payments',
                        value: '&pound;' + NumberFormat(payment.amount, 2)
                    });
                });

                outputArray = this.appendVehiclePricingIncludingApplicableGrants(outputArray);

                outputArray.push({
                    title: 'Amount Financed',
                    value: '&pound;' + NumberFormat(this.quote.Quote.CashDeposit, 2)
                });

                if (this.quoteWithPredictableKeys.depCon > 0) {
                    outputArray.push({
                        title: this.depConName,
                        value: '&pound;' + NumberFormat(this.quoteWithPredictableKeys.depCon, 2)
                    });
                }


                outputArray.push({
                    title: 'Total Amount Of Credit',
                    value: '&pound;' + NumberFormat(this.quote.Quote.Balance, 2)
                });

                outputArray.push({
                    title: 'Term Of Agreement',
                    value: NumberFormat(this.quote.Quote.Term, 0) + ' months'
                });
                outputArray.push({
                    title: 'Total Charge For Credit',
                    value: '&pound;' + NumberFormat(this.quote.Quote.TotalChargeForCredit, 2)
                });
                outputArray.push({
                    title: 'Total Amount Payable',
                    value: '&pound;' + NumberFormat(this.quote.Quote.TotalAmountPayable, 2)
                });
                if (this.financeModified) {
                    outputArray.push({
                        title: 'APR',
                        value: NumberFormat(this.quoteWithPredictableKeys.apr, 2) + '%'
                    });
                } else {
                    outputArray.push({
                        title: 'Representative APR',
                        value: NumberFormat(this.quoteWithPredictableKeys.apr, 2) + '%'
                    });
                }
                outputArray.push({
                    title: 'Representative APR',
                    value: NumberFormat(this.quote.Quote.RateOfInterest, 2) + '%'
                });

                return outputArray;
            }

            return [];
        },
    },
    created() {
        if (typeof this.$route.query['deposit'] != 'undefined') {
            this.$store.commit('Finance/setSelectedDeposit', parseInt(this.$route.query['deposit']));
        }
        if (typeof this.$route.query['term'] != 'undefined') {
            this.$store.commit('Finance/setSelectedTerm', parseInt(this.$route.query['term']));
        }
        if (typeof this.$route.query['mileage'] != 'undefined') {
            this.$store.commit('Finance/setSelectedMileage', parseInt(this.$route.query['mileage']));
        }
        this.setSelectedFinance(null);
        this.$store.dispatch('Finance/initAvailableParameters')
            .then(r => {
                if (typeof this.$route.query['type'] != 'undefined') {
                    if (this.$route.query['type'].toLowerCase() == 'pch' && this.vehicle.have_pch == 1) {
                        this.setSelectedFinance('pch');
                        return;
                    }
                    if (this.$route.query['type'].toLowerCase() == 'pcp' && this.pcp.available == true) {
                        this.setSelectedFinance('pcp');
                        return;
                    }
                    if (this.$route.query['type'].toLowerCase() == 'hp' && this.hp.available == true) {
                        this.setSelectedFinance('hp');
                        return;
                    }
                }
                if (this.pcp.available == true) {
                    this.setSelectedFinance('pcp');
                    return;
                }
                if (this.hp.available == true) {
                    this.setSelectedFinance('hp');
                    return;
                }
                if (this.vehicle.have_pch == 1) {
                    this.setSelectedFinance('pch');
                    return;
                }
                this.setSelectedFinance(null);
            })
            .catch(e => console.error(e));
    },
    methods: {
        sendPCHEnquiry() {
            this.$emit('apply-for-pch');
        },
        sendEnquiry() {
            this.$emit('apply-for-finance');
        },
        scrollToFinance() {
            this.$emit('scroll-to-valuation');
        },
        setSelectedFinance(type) {
            this.$store.commit('Finance/setSelectedFinance', type);
        },
        closest(numberToMatch, arrayToSearch) {
            let closest = null,
                closestDiff = null;
            const length = arrayToSearch.length;
            for (let index = 0; index < length; index++) {
                const currentIndexValue = arrayToSearch[index];
                if (currentIndexValue == numberToMatch) {
                    return currentIndexValue;
                }
                const diff = Math.abs(numberToMatch - currentIndexValue);
                if (closestDiff == null || diff < closestDiff) {
                    closestDiff = diff;
                    closest = currentIndexValue;
                }
            }
            return closest;
        },
        appendVehiclePricingIncludingApplicableGrants(outputArray) {
            const retailPrice = parseFloat(this.vehicle.pricing_display_price);
            const cashPrice = parseFloat((this.quote.Quote?.CashPrice || retailPrice));

            if (retailPrice !== cashPrice) {
                outputArray.push({
                    title: 'Vehicle Price (OTR)',
                    value: '&pound;' + NumberFormat(retailPrice, 2)
                });

                if (this.quoteWithPredictableKeys.governmentGrant > 0) {
                    outputArray.push({
                        title: 'Government Grant',
                        value: '&pound;' + NumberFormat(this.quoteWithPredictableKeys.governmentGrant, 2)
                    });
                }

                outputArray.push({
                    title: 'Vehicle Selling Price',
                    value: '&pound;' + NumberFormat(cashPrice, 2)
                });
            } else if (this.quoteWithPredictableKeys.governmentGrant > 0 && retailPrice == cashPrice) {
                outputArray.push({
                    title: 'Vehicle Price (OTR)',
                    value: '&pound;' + NumberFormat(retailPrice + this.quoteWithPredictableKeys.governmentGrant, 2)
                });

                outputArray.push({
                    title: 'Government Grant',
                    value: '&pound;' + NumberFormat(this.quoteWithPredictableKeys.governmentGrant, 2)
                });

                outputArray.push({
                    title: 'Vehicle Selling Price',
                    value: '&pound;' + NumberFormat(cashPrice, 2)
                });
            } else {
                outputArray.push({
                    title: 'Vehicle Price',
                    value: '&pound;' + NumberFormat(cashPrice, 2)
                });
            }
            return outputArray;
        }
    },
};
</script>

<style>

</style>


// WEBPACK FOOTER //
// resources/assets/js/new-vue/components/details/FinanceCalculator.vue